import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Building } from 'lucide-react';
import { mockProjects } from '../mock';
import ProjectModal from './ProjectModal';
import Modal from 'react-modal';

const Projects = () => {
   const { t } = useTranslation();
   const [filter, setFilter] = useState('all');
   const [visibleProjects, setVisibleProjects] = useState(6);
   const [selectedProject, setSelectedProject] = useState(null);
   const [isModalOpen, setIsModalOpen] = useState(false);

   useEffect(() => {
     setVisibleProjects(6);
   }, [filter]);

  const filteredProjects = filter === 'all'
    ? mockProjects
    : mockProjects.filter(project => project.type === filter);

  const filters = [
    { id: 'all', label: t('projects.filters.all') },
    { id: 'residential', label: t('projects.filters.residential') },
    { id: 'commercial', label: t('projects.filters.commercial') },
    { id: 'industrial', label: t('projects.filters.industrial') }
  ];

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <section id="proyectos" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('projects.title').split(' ')[0]} <span className="text-red-500">{t('projects.title').split(' ')[1]}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            {t('projects.description')}
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filterOption) => (
              <Button
                key={filterOption.id}
                variant={filter === filterOption.id ? "default" : "outline"}
                onClick={() => setFilter(filterOption.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === filterOption.id 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'border-red-500 text-red-500 hover:bg-red-500 hover:text-white'
                }`}
              >
                {filterOption.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {filteredProjects.slice(0, visibleProjects).map((project) => (
            <Card
              key={project.id}
              className="bg-gray-800 border-gray-700 hover:border-red-500 transition-all duration-300 hover:shadow-xl group overflow-hidden cursor-pointer"
              onClick={() => handleProjectClick(project)}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={t(project.title)}
                  className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-500 bg-gray-800"
                />
                <div className="absolute top-4 left-4">
                  <Badge
                    variant="secondary"
                    className="bg-red-600 text-white border-0 font-medium"
                  >
                    {t(`projects.filters.${project.type}`)}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-4">
                <CardTitle className="text-2xl text-white group-hover:text-red-500 transition-colors duration-300">
                  {t(project.title)}
                </CardTitle>
                <div className="flex items-center space-x-2 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>{t(project.location)}</span>
                </div>
              </CardHeader>

              <CardContent>
                <CardDescription className="text-gray-300 mb-6 leading-relaxed">
                  {t(project.description)}
                </CardDescription>

              </CardContent>
            </Card>
          ))}
        </div>

        {/* Load More Button */}
        {visibleProjects < filteredProjects.length && (
          <div className="text-center mt-8">
            <Button
              onClick={() => setVisibleProjects(prev => prev + 6)}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              {t('projects.loadMore')}
            </Button>
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 border border-gray-600 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('projects.cta.title')}
            </h3>
            <p className="text-gray-300 mb-6 text-lg">
              {t('projects.cta.description')}
            </p>
            <button
              onClick={() => {
                const element = document.querySelector('#contacto');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              {t('projects.cta.button')}
            </button>
          </div>
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        project={selectedProject}
      />
    </section>
  );
};

export default Projects;